import clubAPI from "../../../api/clubAPI";
import {
  carouselsFailed,
  carouselsLoaded,
  carouselsRequested,
  carouselsRevalidated,
} from "../../store/homeCarouselsSlice";
import { distanceInMeters } from "../../utils/geo";
import { HOME_CAROUSELS } from "./carouselTypes";

// Distance is computed once here, not per render: a round is always fetched for
// one fixed set of coordinates.
const normalizeClubs = (clubs, coords) => {
  if (!Array.isArray(clubs)) return [];
  return clubs.map((club) => ({
    ...club,
    distanceMeters: distanceInMeters(coords, { latitude: club.Latitude, longitude: club.Longitude }),
  }));
};

// One response carries every carousel: [{ CarouselID, CarouselName, Clubs }].
// Rows are matched by CarouselID; ids the app doesn't know are dropped (a new
// backend carousel must not render untitled), and rows the response doesn't
// include come back ready-and-empty, which the row renders as hidden.
const rowsFromResponse = (rawData, coords) => {
  const parsed = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
  const carousels = Array.isArray(parsed) ? parsed : [];

  const rows = HOME_CAROUSELS.reduce((acc, { key }) => {
    acc[key] = { status: "ready", items: [] };
    return acc;
  }, {});

  carousels.forEach((entry) => {
    const row = HOME_CAROUSELS.find(({ id }) => id === entry?.CarouselID);
    if (!row) {
      console.warn(`GetCarousels returned unknown CarouselID ${entry?.CarouselID} (${entry?.CarouselName})`);
      return;
    }
    rows[row.key] = { status: "ready", name: entry.CarouselName, items: normalizeClubs(entry.Clubs, coords) };
  });

  return rows;
};

// A string, not the coords object: object identity changes every render, a string
// doesn't. 4 dp ≈ 11 m, well under low-accuracy jitter, so a stationary user never
// refetches.
export const locationKeyOf = (coords) =>
  coords ? `${coords.latitude.toFixed(4)},${coords.longitude.toFixed(4)}` : "none";

// Below this, new coordinates are the same place: no ranking changes at 100m, so a
// refetch would spend a request to repaint identical rows. Matches the threshold
// commitGpsFix (v2/utils/address) uses for the same judgement.
const SAME_PLACE_METERS = 100;

/**
 * Whether coordinates are close enough to an already-fetched locationKey that a
 * refetch would change nothing.
 *
 * This exists because the exact-key check alone systematically double-fetched: the
 * welcome prefetch runs on the *address* coordinates, the CTA's GPS fix lands a few
 * dozen metres away — a geocoded street and a live fix are almost never within the
 * key's 11m — and the key mismatch fired a second full round whose predecessor was
 * then discarded. Callers use this beside the `===` check, never instead of it.
 *
 * "none" and empty inputs are never the same place: gaining coordinates for the
 * first time is exactly when a refetch is wanted.
 */
export const isSamePlace = (fetchedKey, coords) => {
  if (!fetchedKey || fetchedKey === "none" || !coords) return false;
  const [latitude, longitude] = fetchedKey.split(",").map(Number);
  const moved = distanceInMeters({ latitude, longitude }, coords);
  return moved !== null && moved < SAME_PLACE_METERS;
};

// Module scope, not a ref: welcome prefetches this round and home can start
// another one for a newer location, in different component trees. A per-hook
// counter couldn't tell those two apart, so a slow earlier round could overwrite
// the newer one's rows.
let runId = 0;

// The round currently in flight, by locationKey — the same shape as fetchAppParams'
// inFlight promise. The store's fetchedForLocation already dedupes callers that read
// it, but it is claimed by a dispatch: a caller deciding from a stale render capture,
// or a future caller that skips the store check, would still double-fire without this.
let inFlight = { key: null, promise: null };

/**
 * Fetches all home carousels in a single request and writes the rows into the
 * store.
 *
 * Lives outside the hook so the welcome screen can start the round while the user
 * is still reading the intro — home then mounts on a warm cache and issues
 * nothing (`carouselsRequested` claims the locationKey at request time, so the
 * cache check holds even while the call is in flight).
 *
 * @param {Object} params
 * @param {{latitude: number, longitude: number}|null} params.coords
 * @param {"cold"|"revalidate"} params.mode "cold" resets the rows to skeletons;
 *   "revalidate" leaves the current rows up and swaps them when the data lands.
 * @param {Function} params.dispatch
 * @param {string} params.token
 * @param {string} params.id
 * @param {string} params.phone
 * @returns {Promise<void>} Never rejects.
 */
export default function fetchHomeCarousels({ coords, mode, dispatch, token, id, phone }) {
  const locationKey = locationKeyOf(coords);

  // A round for these exact coordinates is already in flight — join it rather than
  // firing a duplicate request whose twin would only be discarded by runId.
  if (inFlight.key === locationKey && inFlight.promise) return inFlight.promise;

  const myRun = ++runId;
  dispatch(mode === "revalidate" ? carouselsRevalidated(locationKey) : carouselsRequested(locationKey));

  const load = async () => {
    try {
      const response = await clubAPI.getCarousels({
        token,
        id,
        phone,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      });
      if (myRun !== runId) return;
      if (response.data.Error !== 0) {
        throw { code: response.data.Error, mess: response.data.Message };
      }
      dispatch(carouselsLoaded({ rows: rowsFromResponse(response.data.Data, coords) }));
    } catch (err) {
      if (myRun !== runId) return;
      // Deliberately not swalDialog — the failure surfaces as the in-page error
      // state (HOME_CAROUSELS_ERROR + retry), not a modal over the home page.
      console.error("Home carousels failed:", err);
      dispatch(carouselsFailed());
    }
  };

  // load never rejects; the .then still runs on the failure path.
  const promise = load().then(() => {
    // Clear only our own claim: a newer round for a different key has already
    // overwritten it, and must not be un-claimed by its predecessor finishing.
    if (inFlight.key === locationKey) inFlight = { key: null, promise: null };
  });

  inFlight = { key: locationKey, promise };
  return promise;
}
